/**
 * Fase B — Shop Customer auth context.
 *
 * Strikt gescheiden van bestaande admin AuthContext. Heeft eigen
 * localStorage-key `shop_customer_token`, eigen axios-instance, eigen
 * `useShopAuth()` hook. Een ingelogde admin is GEEN ingelogde klant en
 * vice versa — geen kruisbestuiving.
 *
 * Token-shape: Bearer JWT met `aud: "shop_customer"` (backend gevalideerd).
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const TOKEN_KEY = "shop_customer_token_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const shopApi = axios.create({ baseURL: API });
shopApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const ShopAuthContext = createContext(null);

export function ShopAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    shopApi.get("/shop/auth/me")
      .then((r) => setCustomer(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setCustomer(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await shopApi.post("/shop/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, r.data.access_token);
    setCustomer(r.data.customer);
    return r.data.customer;
  };

  const register = async (payload) => {
    const r = await shopApi.post("/shop/auth/register", payload);
    localStorage.setItem(TOKEN_KEY, r.data.access_token);
    setCustomer(r.data.customer);
    return r.data.customer;
  };

  // Same shop JWT/session; only accepted after the Agent membership/link guard.
  const loginAgent = async (email, password, publicId) => {
    const r = await shopApi.post("/agent-login/login", {email, password, public_id:publicId});
    localStorage.setItem(TOKEN_KEY, r.data.access_token);
    setCustomer(r.data.customer);
    return r.data.customer;
  };

  const logout = async () => {
    try { await shopApi.post("/shop/auth/logout"); } catch (_) {}
    localStorage.removeItem(TOKEN_KEY);
    setCustomer(null);
  };

  const refresh = async () => {
    try {
      const r = await shopApi.get("/shop/auth/me");
      setCustomer(r.data);
      return r.data;
    } catch (_) {
      return null;
    }
  };

  return (
    <ShopAuthContext.Provider value={{ customer, loading, login, loginAgent, register, logout, refresh }}>
      {children}
    </ShopAuthContext.Provider>
  );
}

export function useShopAuth() {
  const ctx = useContext(ShopAuthContext);
  if (!ctx) throw new Error("useShopAuth must be used inside ShopAuthProvider");
  return ctx;
}
